import { Icon, If, location, onCleanup, reactive } from 'cdui-js';
import {
  checkAccountExists,
  forgetPassword,
  PASSWORD_ENCRYPT_TYPE_AES_USERNAME_TS,
  sendSliderCaptcha,
  USE_TYPE_FORGET_PASSWORD,
  userCheck,
  verifyCheckCode,
  verifySendCode,
  encryptForgetLoginPasswordAt,
} from '../../../../../api/login';
import type { UserImageVerificationRespDTO } from '../../../../../api/login';
import i18n from '../../../../../i18n';
import { ensureOpsPublicKeyRegistered } from '../../../../../lib/ensureOpsPublicKey';
import { beginOpsSignedRequestAnonymous } from '../../../../../lib/opsDeviceSign';
import { userState } from '../../../../../stores/user';
import { showActionSheet } from '../../../components/ActionSheet';
import {
  Countries,
  countryValueFromDial,
  dialFromCountryValue,
  toDialCode,
} from '../../../components/SelectCountry/Component';
import { SlideCaptcha } from '../../../components/SlideCaptcha';
import { showToast } from '../../../components/Toast';
import { EyeIcon } from '../../../icons';
import { replaceTemplate } from '@china-difi/lib/template';
import './index.css';

const OTP_LEN = 6;
const RESEND_SECONDS = 60;
const ACCOUNT_TYPE_PHONE = '0';
const FORGET_ACCOUNT_TYPE = '2';

let countdownTimer: ReturnType<typeof setInterval> | null = null;

const focusOtpInput = () => {
  (document.getElementById('forget-pwd-otp-input') as HTMLInputElement | null)?.focus();
};

const toastClientError = (e: unknown, fallback: string) => {
  if (e instanceof Error) {
    showToast({ message: e.message || fallback, duration: 3000 });
  }
};

/** 手机号校验：仅中国大陆（+86）校验 11 位；其它地区只要求非空数字 */
const isValidMobilePhone = (phone: string, areaCode: string): boolean => {
  const p = phone.trim();
  if (!p || !/^\d+$/.test(p)) return false;
  if (areaCode === '+86') return p.length === 11;
  return true;
};

const isValidNewPassword = (pwd: string) => {
  if (pwd.length < 8 || pwd.length > 24) return false;
  return /[A-Za-z]/.test(pwd) && /\d/.test(pwd);
};

const maskPhone = (areaCode: string, phone: string): string => {
  const p = phone.trim();
  if (p.length <= 4) return `${areaCode} ${p}`;
  return `${areaCode} ${p.slice(0, 3)}****${p.slice(-2)}`;
};

const isValidSlideCaptcha = (captcha: UserImageVerificationRespDTO | null | undefined) =>
  !!(captcha?.validateCode && captcha?.shadeImage && captcha?.cutoutImage);

const OtpBox = (props: { char: string; isCurrent: boolean }) => (
  <div
    class={`forget-pwd-otp-box round-XL flex align-center justify-center${
      props.isCurrent ? ' forget-pwd-otp-box-active' : ''
    }`}
  >
    <span class="forget-pwd-otp-digit color-D">{props.char}</span>
  </div>
);

export const ForgetPwd = () => {
  const state = reactive({
    step: 1 as 1 | 2 | 3,
    areaCode: '+86',
    phone: '',
    otpCode: '',
    countdown: RESEND_SECONDS,
    canResend: false,
    verifyCode: '',
    newPwd: '',
    confirmPwd: '',
    showPwd: false,
    showConfirmPwd: false,
    submitting: false,
    verifying: false,
    resetting: false,
    slide: {
      visible: false,
      captcha: null as UserImageVerificationRespDTO | null,
      pending: null as null | {
        account: string;
        countryCode: string;
        sendCodeType: '0';
        useType: number;
      },
      lastResp: null as unknown,
      afterSuccess: null as null | ((resp: unknown) => void),
    },
  });

  const stopCountdown = () => {
    if (countdownTimer) {
      clearInterval(countdownTimer);
      countdownTimer = null;
    }
  };

  const startCountdown = () => {
    stopCountdown();
    state.canResend = false;
    state.countdown = RESEND_SECONDS;
    countdownTimer = setInterval(() => {
      if (state.countdown > 1) {
        state.countdown = state.countdown - 1;
      } else {
        stopCountdown();
        state.countdown = 0;
        state.canResend = true;
      }
    }, 1000);
  };

  onCleanup(() => stopCountdown());

  let actionSheetIns: { close: () => void } | null = null;
  const openAreaCode = () => {
    const saved = state.areaCode;
    const current = countryValueFromDial(saved) || saved;
    actionSheetIns = showActionSheet({
      title: () => <div>{i18n.Login.SelectCountryRegion}</div>,
      contentClass: 'action-sheet--select-country',
      content: () => (
        <Countries
          value={current}
          showDialCode
          change={(e) => {
            const dial = dialFromCountryValue(e);
            state.areaCode = dial || (/^\d+$/.test(String(e)) ? toDialCode(e) : String(e));
            actionSheetIns?.close();
          }}
        />
      ),
    });
  };

  const closeSlideCaptcha = () => {
    state.slide.visible = false;
    state.slide.captcha = null;
    state.slide.pending = null;
    state.slide.lastResp = null;
    state.slide.afterSuccess = null;
  };

  const openSlideCaptchaForSend = (
    captcha: UserImageVerificationRespDTO,
    opts: {
      account: string;
      countryCode: string;
      onSent: (resp: unknown) => void;
    },
  ) => {
    state.slide.pending = {
      account: opts.account,
      countryCode: opts.countryCode,
      sendCodeType: '0',
      useType: USE_TYPE_FORGET_PASSWORD,
    };
    state.slide.afterSuccess = opts.onSent;
    state.slide.lastResp = null;
    state.slide.captcha = captcha;
    state.slide.visible = true;
  };

  const sendCodeWithOptionalSlide = async (opts: {
    account: string;
    countryCode: string;
    onSent: (resp: unknown) => void;
  }) => {
    const checkResult = await userCheck({
      account: opts.account,
      sendCodeType: '0',
      countryCode: opts.countryCode || undefined,
    });

    const sendPayload = {
      sendCodeType: '0' as const,
      account: opts.account,
      contact: opts.account,
      countryCode: opts.countryCode || undefined,
      useType: USE_TYPE_FORGET_PASSWORD,
    };

    if (!checkResult?.slideCaptchaRequired) {
      const resp = await verifySendCode(sendPayload);
      opts.onSent(resp);
      return;
    }

    let captcha: UserImageVerificationRespDTO | null = checkResult?.slideCaptcha ?? null;
    if (!isValidSlideCaptcha(captcha)) {
      captcha = await sendSliderCaptcha();
    }
    if (!isValidSlideCaptcha(captcha)) {
      showToast(i18n.Login.SlideCaptchaUnavailable);
      return;
    }
    openSlideCaptchaForSend(captcha as UserImageVerificationRespDTO, opts);
  };

  const handleSlideSubmit = async (payload: { validateCode: string; slideX: number }) => {
    const pending = state.slide.pending;
    if (!pending) throw new Error(i18n.Login.SlideCaptchaUnavailable);
    const resp = await verifySendCode({
      sendCodeType: pending.sendCodeType,
      account: pending.account,
      contact: pending.account,
      countryCode: pending.countryCode || undefined,
      useType: pending.useType,
      validateCode: payload.validateCode,
      slideX: payload.slideX,
    });
    state.slide.lastResp = resp;
  };

  const handleSlideRefresh = async () => {
    const captcha = await sendSliderCaptcha();
    if (!isValidSlideCaptcha(captcha)) {
      showToast(i18n.Login.SlideCaptchaUnavailable);
      throw new Error(i18n.Login.SlideCaptchaUnavailable);
    }
    state.slide.captcha = captcha;
  };

  const handleSlideSuccess = () => {
    const after = state.slide.afterSuccess;
    const resp = state.slide.lastResp;
    closeSlideCaptcha();
    after?.(resp);
  };

  const enterOtpStep = () => {
    state.otpCode = '';
    state.verifyCode = '';
    state.step = 2;
    startCountdown();
    setTimeout(focusOtpInput, 100);
  };

  const handlePrimaryNext = async () => {
    if (state.submitting) return;
    const phone = state.phone.trim();
    const countryCode = state.areaCode;
    if (!isValidMobilePhone(phone, countryCode)) {
      showToast(i18n.ForgotPassword.EnterValidPhone);
      return;
    }

    state.submitting = true;
    try {
      const exists = await checkAccountExists({
        account: phone,
        validType: '0',
        countryCode,
      });
      if (!exists) {
        showToast(i18n.ForgotPassword.PhoneNotRegistered);
        return;
      }

      await sendCodeWithOptionalSlide({
        account: phone,
        countryCode,
        onSent: () => {
          enterOtpStep();
        },
      });
    } catch (e: unknown) {
      toastClientError(e, i18n.ForgotPassword.SendCodeFailed);
    } finally {
      state.submitting = false;
    }
  };

  const handleResend = async () => {
    if (!state.canResend || state.submitting) return;
    const phone = state.phone.trim();
    state.submitting = true;
    try {
      await sendCodeWithOptionalSlide({
        account: phone,
        countryCode: state.areaCode,
        onSent: () => {
          state.otpCode = '';
          startCountdown();
          showToast(i18n.Login.CodeResent);
          setTimeout(focusOtpInput, 100);
        },
      });
    } catch (e: unknown) {
      toastClientError(e, i18n.ForgotPassword.SendCodeFailed);
    } finally {
      state.submitting = false;
    }
  };

  const handleOtpInput = async (e: Event) => {
    const val = (e.target as HTMLInputElement).value.replace(/\D/g, '').slice(0, OTP_LEN);
    state.otpCode = val;
    if (val.length !== OTP_LEN || state.verifying) return;

    state.verifying = true;
    try {
      const phone = state.phone.trim();
      const result = await verifyCheckCode({
        account: phone,
        sendCodeType: '0',
        accountType: ACCOUNT_TYPE_PHONE,
        verifyCode: val,
        countryCode: state.areaCode,
      });
      if (!result?.verifyCodeCorrect) {
        showToast(i18n.ForgotPassword.VerifyCodeError);
        state.otpCode = '';
        setTimeout(focusOtpInput, 50);
        return;
      }
      state.verifyCode = val;
      state.step = 3;
      state.newPwd = '';
      state.confirmPwd = '';
    } catch (err: unknown) {
      toastClientError(err, i18n.ForgotPassword.VerifyFailed);
      state.otpCode = '';
    } finally {
      state.verifying = false;
    }
  };

  /** 重置成功：清本地登录态，离开公开路径并强制打开登录层 */
  const goToLoginAfterReset = () => {
    localStorage.removeItem('token');
    userState.isLogin = false;
    userState.icon = '';
    // 必须先离开 /forget-pwd（公开页），否则 Login 层不会盖上
    location.routeTo('/');
    userState.showLoginPage = true;
  };

  /** 顶部返回离开本页：优先回上一页；未登录时打开登录层 */
  const leaveForgetPwd = () => {
    if (!userState.isLogin) {
      userState.showLoginPage = true;
    }
    if (window.history.length > 1) {
      history.back();
    } else {
      location.routeTo('/');
    }
  };

  const handleResetSubmit = async () => {
    if (state.resetting) return;
    const password = state.newPwd;
    const confirm = state.confirmPwd;
    if (!isValidNewPassword(password)) {
      showToast(i18n.ForgotPassword.PasswordRuleTip);
      return;
    }
    if (password !== confirm) {
      showToast(i18n.ForgotPassword.PasswordMismatch);
      return;
    }
    const verifyCode = state.verifyCode.trim();
    if (!verifyCode) {
      showToast(i18n.ForgotPassword.OtpExpired);
      state.step = 2;
      return;
    }

    const phone = state.phone.trim();
    state.resetting = true;
    let resetOk = false;
    try {
      await ensureOpsPublicKeyRegistered();
      const signed = await beginOpsSignedRequestAnonymous({
        method: 'POST',
        path: '/user/forgetPassword',
      });
      const enc = encryptForgetLoginPasswordAt(phone, password, signed.timestamp);
      await forgetPassword(
        {
          account: phone,
          newPassword: enc.encrypted,
          validType: '0',
          verifyCode,
          accountType: FORGET_ACCOUNT_TYPE,
          encryptType: PASSWORD_ENCRYPT_TYPE_AES_USERNAME_TS,
          timestamp: enc.timestamp,
          countryCode: state.areaCode,
        },
        { headers: signed.headers },
      );
      // 请求成功（HTTP/业务通过拦截器）即视为完成，强制去登录
      resetOk = true;
    } catch (e: unknown) {
      // 失败：留在设密页提示，不回验证码步
      toastClientError(e, i18n.ForgotPassword.ResetFailed);
    } finally {
      state.resetting = false;
    }

    if (resetOk) {
      showToast({ message: i18n.ForgotPassword.ResetSuccess, type: 'success' });
      goToLoginAfterReset();
    }
  };

  const handleBack = () => {
    if (state.step > 1) {
      if (state.step === 2) stopCountdown();
      state.step = (state.step - 1) as 1 | 2 | 3;
      return;
    }
    leaveForgetPwd();
  };

  const canPrimary = () => isValidMobilePhone(state.phone, state.areaCode) && !state.submitting;
  const canReset = () =>
    isValidNewPassword(state.newPwd) &&
    state.newPwd === state.confirmPwd &&
    !state.resetting;

  return (
    <div class="forget-pwd flex flex-column bg-c">
      <div class="forget-pwd-topbar relative flex align-center">
        <div class="forget-pwd-back absolute flex align-center justify-center" onclick={handleBack}>
          <Icon name="common-back-arrow" class="icon-c-D" />
        </div>
        <span class="forget-pwd-title font-s-L absolute" style={{ left: '0', right: '0', 'text-align': 'center' }}>
          {i18n.ForgotPassword.Title}
        </span>
      </div>

      {/* Step 1：手机号（按钮在根级 footer，避免嵌套 flex 把按钮挤出视口） */}
      <If when={state.step === 1}>
        <div class="flex flex-column padding-m-x" style={{ gap: '16px', 'padding-top': '20px' }}>
          <span class="font-s color-D">{i18n.ForgotPassword.VerifyPhoneTitle}</span>
          <div class="forget-pwd-input round-XL flex align-center padding-x padding-y-XS" style={{ gap: '10px' }}>
            <div class="flex align-center" style={{ gap: '4px', 'flex-shrink': '0' }} onclick={openAreaCode}>
              <span class="font-s color-D">{state.areaCode}</span>
              <Icon name="common-arrow-down" class="icon-c-D" />
            </div>
            <div class="forget-pwd-sep" />
            <input
              class="forget-pwd-input-field flex-1 font-s color-D"
              type="tel"
              placeholder={i18n.ForgotPassword.PhonePlaceholder}
              value={state.phone}
              oninput={(e: Event) => {
                state.phone = (e.target as HTMLInputElement).value;
              }}
            />
          </div>
        </div>
      </If>

      {/* Step 2：OTP 输满自动校验，无底部按钮 */}
      <If when={state.step === 2}>
        <div class="flex flex-column padding-m-x" style={{ gap: '16px', 'padding-top': '20px' }}>
          <div class="flex flex-column" style={{ gap: '4px' }}>
            <span class="font-s color-D">{i18n.ForgotPassword.GetCodeTitle}</span>
            <span class="font-s-S color">{replaceTemplate(i18n.Login.SmsCodeLimitTip, 8, 30)}</span>
          </div>
          <span class="font-s-S color">
            {i18n.Login.SmsCodeSentTip} {maskPhone(state.areaCode, state.phone)}
          </span>
          <div class="flex flex-column" style={{ gap: '15px' }}>
            <div class="forget-pwd-otp-row" onclick={focusOtpInput}>
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <OtpBox
                  char={state.otpCode[i] || ''}
                  isCurrent={state.otpCode.length === i || (i === 5 && state.otpCode.length >= 5)}
                />
              ))}
            </div>
            <input
              id="forget-pwd-otp-input"
              type="tel"
              maxlength="6"
              autocomplete="one-time-code"
              value={state.otpCode}
              oninput={handleOtpInput}
              style={{
                position: 'fixed',
                left: '-9999px',
                top: '0',
                opacity: '0',
                'pointer-events': 'none',
                width: '1px',
                height: '1px',
              }}
            />
            <div class="flex" style={{ 'justify-content': 'flex-end' }}>
              <span
                class="font-s-S"
                style={{
                  color: state.canResend ? '#FF4000' : '#1B212D',
                  cursor: state.canResend ? 'pointer' : 'default',
                }}
                onclick={() => state.canResend && handleResend()}
              >
                {state.canResend
                  ? i18n.Login.ResendVerifyCode
                  : `${state.countdown}${i18n.ForgotPassword.ResendCountdownSuffix}`}
              </span>
            </div>
            <If when={state.verifying}>
              <span class="font-s-S color">{i18n.Login.VerifyingCode}</span>
            </If>
          </div>
        </div>
      </If>

      {/* Step 3：设新密码 */}
      <If when={state.step === 3}>
        <div class="flex flex-column padding-m-x" style={{ gap: '16px', 'padding-top': '20px' }}>
          <span class="font-s color-D">{i18n.ForgotPassword.SetPasswordTitle}</span>
          <div class="flex flex-column" style={{ gap: '4px' }}>
            <div class="forget-pwd-input round-XL flex align-center padding-x padding-y-XS" style={{ gap: '10px' }}>
              <input
                class="forget-pwd-input-field flex-1 font-s"
                type={state.showPwd ? 'text' : 'password'}
                placeholder={i18n.ForgotPassword.PasswordPlaceholder}
                value={state.newPwd}
                oninput={(e: Event) => {
                  state.newPwd = (e.target as HTMLInputElement).value;
                }}
              />
              <div onclick={() => (state.showPwd = !state.showPwd)}>
                <EyeIcon visible={state.showPwd} />
              </div>
            </div>
            <span class="font-s-S color-L">{i18n.ForgotPassword.PasswordRuleTip}</span>
          </div>
          <div class="forget-pwd-input round-XL flex align-center padding-x padding-y-XS" style={{ gap: '10px' }}>
            <input
              class="forget-pwd-input-field flex-1 font-s"
              type={state.showConfirmPwd ? 'text' : 'password'}
              placeholder={i18n.ForgotPassword.ConfirmPasswordPlaceholder}
              value={state.confirmPwd}
              oninput={(e: Event) => {
                state.confirmPwd = (e.target as HTMLInputElement).value;
              }}
            />
            <div onclick={() => (state.showConfirmPwd = !state.showConfirmPwd)}>
              <EyeIcon visible={state.showConfirmPwd} />
            </div>
          </div>
        </div>
      </If>

      <div style={{ flex: '1' }} />

      <If when={state.step === 1}>
        <div class="forget-pwd-footer bg-c flex flex-column" style={{ gap: '12px' }}>
          <button
            class="primary-btn btn-rounded flex align-center justify-center"
            style={{ height: '50px', width: '100%' }}
            disabled={!canPrimary()}
            aria-busy={state.submitting}
            onclick={() => canPrimary() && handlePrimaryNext()}
          >
            {state.submitting ? i18n.Login.Sending : i18n.Login.NextStep}
          </button>
        </div>
      </If>

      <If when={state.step === 3}>
        <div class="forget-pwd-footer bg-c flex flex-column">
          <button
            class="primary-btn btn-rounded flex align-center justify-center"
            style={{ height: '50px', width: '100%' }}
            disabled={!canReset()}
            aria-busy={state.resetting}
            onclick={() => canReset() && handleResetSubmit()}
          >
            {state.resetting ? i18n.ForgotPassword.Submitting : i18n.ForgotPassword.SubmitBtn}
          </button>
        </div>
      </If>

      <SlideCaptcha
        visible={state.slide.visible}
        captcha={state.slide.captcha}
        onCancel={closeSlideCaptcha}
        onSubmit={handleSlideSubmit}
        onRefresh={handleSlideRefresh}
        onSuccess={handleSlideSuccess}
      />
    </div>
  );
};
